import type { BattleRes, SupCat } from './constants.ts';
import { MILLI } from './quantity.ts';
import type { CrateType, ItemUnit, LotPlace, Place } from './schemas.ts';

// Sierra's stockpile rules as pure functions (plan 2B "Sierra's rules", R1–R3 and
// R11), ported from sierra-panel.html at the lines given. tests/rules.test.ts holds
// the plan's tables.

/** A stockpile location's key: the text trimmed and lower-cased. "Area18 " and "area18" are one place. */
export function locationKey(location: string): string {
  return location.trim().toLowerCase();
}

/** One string per place: `ship:<id>` or `at:<location key>`. */
export function placeKey(place: Place | LotPlace): string {
  return place.kind === 'ship' ? `ship:${place.shipId}` : `at:${locationKey(place.location)}`;
}

/** Whether two places are the same. Aboard a ship only the ship counts (the panel's crateKey, line 669). */
export function samePlace(a: Place | LotPlace, b: Place | LotPlace): boolean {
  return placeKey(a) === placeKey(b);
}

/** What's measured against a prep level: SCU (in thousandths), kits or units. */
export type Measure = 'scu' | 'kits' | 'units';

/** Crates are measured in SCU through their size (Assumption 4). */
export function measureOf(unit: ItemUnit): Measure {
  return unit === 'crates' ? 'scu' : unit;
}

/** The lot fields the rules read. */
export interface LotLike {
  item: string;
  place: Place | LotPlace;
  crateSize: number | null;
  refined: boolean;
  amount: number;
}

/** What one lot counts towards its item's measure: a crate lot's SCU, or its amount. */
export function measured(unit: ItemUnit, lot: Pick<LotLike, 'crateSize' | 'amount'>): number {
  return unit === 'crates' ? (lot.crateSize ?? 0) * MILLI * lot.amount : lot.amount;
}

/**
 * R1, a place's "have" for an item's prep level (`haveOf`, line 671). Crates: the
 * SCU of every lot of the item at the place, across all sizes. Materials: the
 * refined lots, any quality (port: the panel's minimum sat on one lot). Everything
 * else: every lot.
 */
export function haveOf(item: { id: string; category: SupCat; unit: ItemUnit }, place: Place | LotPlace, lots: readonly LotLike[]): number {
  return lots
    .filter((lot) => lot.item === item.id && samePlace(lot.place, place))
    .filter((lot) => item.category !== 'Materials' || lot.refined)
    .reduce((sum, lot) => sum + measured(item.unit, lot), 0);
}

/** A prep level's state: none set, low (short, aboard a ship), or at it. */
export type PrepState = 'none' | 'low' | 'ok';

/**
 * R2 (`isLow`, line 673). A stockpile is low at or below its target; a ship is short
 * only below it, since its level is one to fill to. A target of 0 is no prep level.
 */
export function prepState(placeKind: Place['kind'], have: number, target: number | null | undefined): PrepState {
  if (target === null || target === undefined || target <= 0) return 'none';
  const low = placeKind === 'ship' ? have < target : have <= target;
  return low ? 'low' : 'ok';
}

/** The fields that make a stack, as stored: never null, so the unique index sees equal keys as equal. */
export interface StackKey {
  item: string;
  /** A ship's id, or ''. */
  ship: string;
  /** A stockpile location's key, or '' aboard a ship. */
  locationKey: string;
  /** 0 when not a crate. */
  crateSize: number;
  /** −1 when unknown or not a material: null ≠ 0 ≠ 500. */
  quality: number;
  refined: boolean;
  refinedBy: string;
  holder: string;
}

/** The stored stand-in for an unknown quality. */
export const NO_QUALITY = -1;

/**
 * R3, the stacking key (`cratesSheet`, line 1820; `matsSheet`, line 1795;
 * `stockRefined`, line 2388): item, place, crate size, quality, refined, refined by
 * and holder. The holder is in it (port: the panel stacked on everything but), so
 * stock someone holds personally doesn't merge with the org's.
 */
export function stackKey(lot: { item: string; place: Place | LotPlace; crateSize: number | null; quality: number | null; refined: boolean; refinedBy: string | null; holder: string | null }): StackKey {
  return {
    item: lot.item,
    ship: lot.place.kind === 'ship' ? lot.place.shipId : '',
    locationKey: lot.place.kind === 'stockpile' ? locationKey(lot.place.location) : '',
    crateSize: lot.crateSize ?? 0,
    quality: lot.quality ?? NO_QUALITY,
    refined: lot.refined,
    refinedBy: lot.refinedBy ?? '',
    holder: lot.holder ?? '',
  };
}

/** One string per stack, for maps. */
export function stackId(key: StackKey): string {
  return JSON.stringify([key.item, key.ship, key.locationKey, key.crateSize, key.quality, key.refined, key.refinedBy, key.holder]);
}

/**
 * R11, the crate type the item form suggests from a name (`crateGuess`, line 520),
 * ported exactly, quirks included: "armed" matches /med/, since it only suggests.
 */
export function crateGuess(name: string): CrateType {
  const n = name.toLowerCase();
  if (/\brmc\b|construction|\bcm\b|repair|recycled/.test(n)) return 'Repair materials';
  if (/ammo|missile|torpedo|ordnance|round|rocket|bomb/.test(n)) return 'Ammo & ordnance';
  if (/hydrogen|quantum|fuel/.test(n)) return 'Fuel';
  if (/med|gel|paramed|hemozal|adrenaline/.test(n)) return 'Medical';
  if (/\bkit/.test(n)) return 'Kits';
  if (/component|cooler|shield|power plant|drive|weapon/.test(n)) return 'Components';
  return 'Other';
}

/** R11, the battle resource the item form suggests (`bresGuess`, line 483), or null. */
export function bresGuess(name: string, category: SupCat): BattleRes | null {
  if (/\brmc\b|recycled material/i.test(name)) return 'rmc';
  if (/hydrogen/i.test(name)) return 'hyd';
  if (/quantum/i.test(name) && (category === 'Fuel' || /fuel/i.test(name))) return 'qt';
  const size = /\b(?:s|size)\s*-?\s*([2-5])\b/i.exec(name);
  if (size !== null && (category === 'Ammo & ordnance' || /ammo|round|ballistic|mag/i.test(name))) return `s${size[1] ?? ''}` as BattleRes;
  return null;
}
